import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { useSubscription } from '../contexts/SubscriptionContext';

const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const sessionId = searchParams.get('session_id');
  
  // 🆕 구독 상태 새로고침을 위한 hook
  const { refreshSubscription } = useSubscription();

  // 안전한 번역 함수
  const safeT = (key: string, fallback: string = '') => {
    try {
      const result = t(key);
      return typeof result === 'string' ? result : fallback;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return fallback;
    }
  };

  // 🆕 결제 성공 시 구독 상태 새로고침
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success') === 'true';
    const sessionId = urlParams.get('session_id');
    
    if (paymentSuccess || sessionId) {
      console.log('🔄 [PaymentSuccessPage] 결제 성공 감지, 구독 상태 새로고침 예약');
      
      // 5초 후 구독 상태 새로고침 (웹훅 처리 시간 충분히 고려)
      const refreshTimer = setTimeout(() => {
        console.log('🔄 [PaymentSuccessPage] 구독 상태 새로고침 실행');
        refreshSubscription();
      }, 5000);
      
      return () => clearTimeout(refreshTimer);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (!userId || !sessionId) {
        setError('Missing user ID or session ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Stripe 세션 정보를 가져와서 구독 상태 업데이트
        console.log('🔍 Verifying Stripe session:', sessionId);
        
        let sessionData;
        let verificationMethod = 'verifySession';
        
        try {
          // 방법 1: verifySession 함수 호출
          const response = await fetch(`https://us-central1-scanvcf-375fb.cloudfunctions.net/verifySession`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
            },
            body: JSON.stringify({ sessionId })
          });

          console.log('📡 Response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ verifySession API Error:', errorText);
            throw new Error(`verifySession failed: ${response.status} - ${errorText}`);
          }
          
          sessionData = await response.json();
          console.log('✅ verifySession success:', sessionData);
          
        } catch (error) {
          console.warn('⚠️ verifySession failed, trying alternative approach:', error);
          
          try {
            // 방법 2: checkPaymentStatus 함수 호출 (대안)
            verificationMethod = 'checkPaymentStatus';
            console.log('🔄 Trying checkPaymentStatus as fallback...');
            
            const checkResponse = await fetch(`https://us-central1-scanvcf-375fb.cloudfunctions.net/checkPaymentStatus?sessionId=${sessionId}`, {
              headers: {
                'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
              }
            });
            
            if (!checkResponse.ok) {
              throw new Error(`checkPaymentStatus failed: ${checkResponse.status}`);
            }
            
            sessionData = await checkResponse.json();
            console.log('✅ checkPaymentStatus success:', sessionData);
            
          } catch (fallbackError) {
            console.error('❌ Both verification methods failed:', fallbackError);
            
            // 방법 3: 기본 구독 정보로 진행 (최후 수단)
            verificationMethod = 'fallback';
            console.log('🆘 Using fallback subscription data...');
            
            sessionData = {
              planType: 'standard', // 기본값
              billingPeriod: 'monthly', // 기본값
              expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30일 후
              customerId: 'fallback',
              subscriptionId: 'fallback'
            };
          }
        }

        setSubscriptionData(sessionData);

        // 사용자 구독 정보 업데이트 (더 안전한 방법)
        const db = getFirestore();
        
        try {
          // 1. 사용자 메인 정보 업데이트
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            subscriptionStatus: 'active',
            subscriptionPlan: sessionData.planType,
            subscriptionExpiresAt: sessionData.expiresAt,
            lastPaymentDate: new Date(),
            stripeCustomerId: sessionData.customerId,
            stripeSubscriptionId: sessionData.subscriptionId,
            verificationMethod: verificationMethod, // 어떤 방법으로 검증했는지 기록
            lastUpdated: new Date()
          });
          
          // 2. 구독 상세 정보를 별도 컬렉션에 저장 (웹훅과 동일한 구조)
          const subscriptionRef = doc(db, 'users', userId, 'subscription', 'status');
          
          // 만료일 계산 (웹훅과 동일한 로직)
          const now = new Date();
          let expiresAt = new Date(now);
          
          if (sessionData.billingPeriod === 'yearly') {
            expiresAt.setFullYear(expiresAt.getFullYear() + 1);
          } else {
            expiresAt.setMonth(expiresAt.getMonth() + 1);
          }
          
          await setDoc(subscriptionRef, {
            plan: sessionData.planType || 'standard',
            subscriptionStatus: 'active', // 필드명 일치
            planExpiresAt: expiresAt, // 올바른 만료일 계산
            lastPaymentAt: new Date(),
            billingPeriod: sessionData.billingPeriod,
            stripeSessionId: sessionId,
            stripeCustomerId: sessionData.customerId,
            stripeSubscriptionId: sessionData.subscriptionId,
            verificationMethod: verificationMethod,
            updatedAt: new Date(),
            createdAt: new Date()
          }, { merge: true });
          
          console.log('✅ User subscription data updated successfully');
          
        } catch (dbError) {
          console.error('❌ Database update failed:', dbError);
          // 데이터베이스 업데이트 실패해도 계속 진행
          // 사용자는 결제 성공 페이지를 볼 수 있음
        }

        console.log('✅ Payment success processed for user:', userId);
        
        // 🆕 결제 성공 후 3초 뒤에 자동으로 대시보드로 이동
        setTimeout(() => {
          console.log('🔄 [PaymentSuccessPage] Auto-redirecting to dashboard after payment success');
          navigate('/dashboard');
        }, 3000);
        
      } catch (error) {
        console.error('❌ Error processing payment success:', error);
        
        let errorMessage = 'Unknown error occurred';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        // 구체적인 에러 메시지 제공
        if (errorMessage.includes('Failed to fetch')) {
          errorMessage = '네트워크 연결을 확인해주세요. Cloud Function이 배포되지 않았을 수 있습니다.';
        } else if (errorMessage.includes('401')) {
          errorMessage = '인증이 만료되었습니다. 다시 로그인해주세요.';
        } else if (errorMessage.includes('404')) {
          errorMessage = 'Stripe 세션을 찾을 수 없습니다. 결제 정보를 확인해주세요.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [userId, sessionId, auth]);

  const handleContinue = () => {
    console.log('🔄 [PaymentSuccessPage] Continue button clicked, navigating to /dashboard');
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{safeT('payment.processing', '결제를 처리하고 있습니다...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
            <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {safeT('payment.error', '결제 처리 오류')}
          </h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate('/paywall')}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {safeT('payment.retry', '다시 시도')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* 성공 아이콘 */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
          <CheckCircleIcon className="h-10 w-10 text-green-600" />
        </div>

        {/* 제목 */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {safeT('payment.success.title', '결제가 완료되었습니다!')}
        </h1>

        {/* 설명 */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {safeT('payment.success.message', '구독이 성공적으로 활성화되었습니다. 이제 모든 기능을 사용할 수 있습니다.')}
        </p>

        {/* 구독 정보 */}
        {subscriptionData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">
              {safeT('payment.subscription.details', '구독 정보')}
            </h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">{safeT('payment.plan', '플랜')}:</span> {subscriptionData.planType}</p>
              <p><span className="font-medium">{safeT('payment.billing', '결제 주기')}:</span> {subscriptionData.billingPeriod}</p>
              <p><span className="font-medium">{safeT('payment.expires', '만료일')}:</span> {new Date(subscriptionData.expiresAt).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {/* 계속하기 버튼 */}
        <button
          onClick={handleContinue}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {safeT('payment.continue', '대시보드로 이동')}
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
